import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Award, ShieldCheck, CheckCircle2, ArrowLeft, ArrowRight, Users, MapPin, Sparkles,
  Lock, Star, FileBadge2, Stamp, Crown, Search, BookOpen, Mail, Building2, ExternalLink, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import SEO from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ItalyMap from "../components/ItalyMap";
import IscrivitiModal from "../components/IscrivitiModal";

const API = process.env.REACT_APP_BACKEND_URL;

const PACCHETTI = [
  {
    id: "tesseramento",
    nome: "Tesseramento A.C.D.T.",
    prezzo: 50.00,
    sottotitolo: "Accesso all'associazione",
    descrizione: "Diventa socio dell'Associazione Consulenti Digitali Tecnologici e accedi a tutti i servizi base.",
    icona: ShieldCheck,
    colore: "from-[#3b82f6] to-[#1d4ed8]",
    badge: "Base",
    features: [
      "Tessera digitale ufficiale + QR code",
      "Accesso all'Area Riservata Soci",
      "Dashboard con tracking CFDT",
      "Catalogo corsi (gratis per Premium)",
      "Forum, eventi e community",
      "News e comunicazioni ufficiali",
      "Validità annuale",
    ],
    cta: "Iscriviti come Socio",
    locked: false,
  },
  {
    id: "registro",
    nome: "Iscrizione al Registro Nazionale",
    prezzo: 69.90,
    sottotitolo: "Riconoscimento professionale",
    descrizione: "Solo per soci che hanno completato il Corso A.C.D.T. per la qualificazione professionale o raggiunto 20 CFDT. Ottieni il riconoscimento ufficiale come Consulente Digitale.",
    icona: Crown,
    colore: "from-[#f59e0b] to-[#d97706]",
    badge: "Riservato",
    features: [
      "Iscrizione al Registro Nazionale ufficiale",
      "Profilo pubblico nella Directory Soci",
      "Carta intestata personalizzata",
      "Timbro digitale ufficiale per firma documenti",
      "Attestato di iscrizione Agg. MIMIT validato",
      "Visibilità nella mappa pubblica dei consulenti",
      "Quota annuale di rinnovo",
    ],
    cta: "Verifica eleggibilità",
    locked: true,
    requisiti: [
      "Tesseramento A.C.D.T. attivo",
      "Corso A.C.D.T. per la qualificazione professionale completato OPPURE 20 CFDT accumulati",
    ],
  },
];

export default function RegistroNazionale() {
  const [iscritti, setIscritti] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [activeProvince, setActiveProvince] = useState(null);
  const [modalPacchetto, setModalPacchetto] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/registro/iscritti`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/api/registro/stats`).then((r) => r.data).catch(() => null),
    ]).then(([list, s]) => {
      setIscritti(list);
      setStats(s);
      setLoading(false);
    });
  }, []);

  const filtered = iscritti.filter((i) => {
    if (activeProvince && i.provincia !== activeProvince) return false;
    const q = filter.toLowerCase();
    return !q || [i.nome, i.cognome, i.citta, i.provincia, i.specializzazione]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <>
      <SEO
        title="Registro Nazionale Consulenti ed Educatori Digitali - A.C.D.T."
        description="Registro Nazionale dei Consulenti ed Educatori Digitali, Tecnologi e di Sostenibilità. Tesseramento e iscrizione ufficiale."
        path="/registro-nazionale"
      />
      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-[#f5f7ff] via-white to-[#f5f7ff]">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#f59e0b]/25 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-[#3b82f6]/25 blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
            <Link to="/" className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#fbbf24] hover:underline">
              <ArrowLeft className="h-4 w-4" /> Torna alla home
            </Link>

            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f59e0b] text-[#0a1020] text-[11px] font-extrabold uppercase tracking-[0.2em]">
                <Award className="h-3.5 w-3.5" /> Registro ufficiale nazionale
              </div>
              <img
                src="/registro-logo.jpeg"
                alt="Logo Registro Professionisti Digitali"
                data-testid="hero-registro-logo"
                className="h-24 w-24 rounded-full border-4 border-white/20 shadow-2xl bg-white object-contain"
              />
            </div>

            <h1 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.05] max-w-4xl">
              Registro Nazionale<br />
              <span className="text-[#fbbf24]">Consulenti ed Educatori</span><br />
              Digitali, Tecnologi e di Sostenibilità
            </h1>

            <p className="mt-6 max-w-2xl text-[15px] md:text-[16.5px] text-slate-200 leading-relaxed">
              Il riconoscimento ufficiale per professionisti della consulenza digitale e dell&apos;educazione tecnologica.
              Aderisci all&apos;A.C.D.T., consegui i requisiti formativi e iscriviti al Registro per essere riconosciuto
              a livello nazionale.
            </p>

            {/* Stats live */}
            {stats && (
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
                <StatBox label="Iscritti totali" value={stats.totale} highlight />
                <StatBox label="Da A.C.D.T." value={stats.da_acdt} />
                <StatBox label="Da Esse Studi" value={stats.da_esse_studi} />
                <StatBox label="Province attive" value={stats.province_attive} />
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#pacchetti" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold transition-colors">
                Vedi pacchetti <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#elenco" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold transition-colors">
                <Users className="h-4 w-4" /> Elenco iscritti
              </a>
            </div>
          </div>
        </section>

        {/* PACCHETTI */}
        <section id="pacchetti" className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[11px] font-extrabold uppercase tracking-[0.2em]">
              <Sparkles className="h-3.5 w-3.5" /> Pacchetti di iscrizione
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">Scegli il tuo percorso</h2>
            <p className="mt-3 text-[14.5px] text-slate-600">
              Inizia con il tesseramento base e, una volta completati i requisiti formativi,
              accedi all&apos;iscrizione al Registro Nazionale.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {PACCHETTI.map((p) => {
              const I = p.icona;
              return (
                <div key={p.id} data-testid={`pacchetto-${p.id}`} className={`rounded-3xl border-2 bg-white p-7 md:p-8 relative ${p.locked ? "border-[#f59e0b]/40" : "border-slate-200"} hover:shadow-xl transition-all`}>
                  {p.locked && (
                    <div className="absolute -top-3 right-6 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1020] text-[10.5px] font-extrabold uppercase tracking-wider">
                      <Lock className="h-3 w-3" /> Riservato
                    </div>
                  )}
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${p.colore} text-white flex items-center justify-center mb-5 shadow-lg`}>
                    <I className="h-7 w-7" />
                  </div>
                  <div className="text-[10.5px] uppercase tracking-[0.2em] font-extrabold text-slate-500">{p.badge}</div>
                  <h3 className="mt-1 text-2xl font-extrabold text-[#0a1f55] leading-tight">{p.nome}</h3>
                  <p className="text-[13.5px] text-slate-500 mt-1">{p.sottotitolo}</p>
                  <p className="mt-4 text-[13.5px] text-slate-700 leading-relaxed">{p.descrizione}</p>

                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="text-5xl font-extrabold text-[#0a1f55]">€{p.prezzo.toFixed(2)}</span>
                    <span className="text-[13px] text-slate-500 font-semibold">/anno</span>
                  </div>

                  <div className="mt-5 pt-5 border-t border-slate-100 space-y-2.5">
                    {p.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2.5 text-[13.5px] text-slate-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  {p.requisiti && (
                    <div className="mt-5 rounded-xl bg-[#f59e0b]/8 border border-[#f59e0b]/30 p-4">
                      <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#b45309] mb-2">Requisiti obbligatori</div>
                      {p.requisiti.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-[12.5px] text-[#7c2d12] font-semibold">
                          <Star className="h-3.5 w-3.5 text-[#f59e0b] flex-shrink-0 mt-0.5 fill-current" />
                          <span>{r}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button data-testid={`btn-acquista-${p.id}`} onClick={() => setModalPacchetto(p)} className={`mt-6 w-full h-12 font-bold ${p.locked ? "bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55]" : "bg-[#102a6b] hover:bg-[#0a1f55] text-white"}`}>
                    {p.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-8 rounded-xl bg-[#102a6b]/5 border border-[#102a6b]/15 p-4 text-[12.5px] text-[#102a6b] max-w-3xl mx-auto text-center">
            🔧 <strong>MOCKUP pagamenti:</strong> i pulsanti di acquisto sono dimostrativi. L&apos;integrazione PayPal/Klarna/Stripe è prevista nelle prossime fasi.
          </div>
        </section>

        {/* INFO COMBINAZIONE INCROCIATA */}
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#102a6b]/8 text-[#102a6b] text-[11px] font-extrabold uppercase tracking-[0.2em]">
                  <Building2 className="h-3.5 w-3.5" /> Combinazione incrociata
                </div>
                <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-[#0a1f55]">
                  Un unico registro, due fonti ufficiali
                </h2>
                <p className="mt-4 text-[14px] text-slate-600 leading-relaxed">
                  L&apos;elenco pubblico del Registro Nazionale è la combinazione automatica di:
                </p>
                <ul className="mt-4 space-y-2 text-[13.5px] text-slate-700">
                  <li className="flex items-start gap-3">
                    <div className="h-7 w-7 rounded-lg bg-[#102a6b] text-white flex items-center justify-center flex-shrink-0 font-extrabold text-[12px]">A</div>
                    <div>
                      <strong>Iscritti A.C.D.T.</strong> — soci che hanno acquistato il pacchetto Registro e completato il corso o raggiunto 20 CFDT.
                      Aggiornato in tempo reale dal nostro database.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="h-7 w-7 rounded-lg bg-[#f59e0b] text-[#0a1020] flex items-center justify-center flex-shrink-0 font-extrabold text-[12px]">E</div>
                    <div>
                      <strong>Iscritti Esse Studi</strong> — professionisti già censiti nel portale ufficiale del{" "}
                      Centro Studi Esse (ASN B0196), validatore MIMIT.
                    </div>
                  </li>
                </ul>
                <a
                  href="https://server.esserete.it/formazionescheda2.html?asn=B0196"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-[13px] font-extrabold text-[#102a6b] hover:underline"
                >
                  Apri portale Esse Studi <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-[#0a1f55] to-[#1e3a8a] text-white p-6">
                <FileBadge2 className="h-8 w-8 text-[#fbbf24]" />
                <h3 className="mt-3 font-extrabold text-lg leading-tight">Aggiornamento automatico</h3>
                <p className="mt-2 text-[12.5px] text-slate-300">
                  Ogni nuova iscrizione approvata aggiorna istantaneamente l&apos;elenco pubblico,
                  garantendo trasparenza e tracciabilità ufficiale.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ELENCO ISCRITTI */}
        <section id="elenco" className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 space-y-8">
          {!loading && iscritti.length > 0 && (
            <ItalyMap iscritti={iscritti} activeProvince={activeProvince} onSelectProvince={setActiveProvince} />
          )}

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#102a6b]/8 text-[#102a6b] text-[11px] font-extrabold uppercase tracking-[0.2em]">
                <Users className="h-3.5 w-3.5" /> Elenco pubblico
              </div>
              <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">Iscritti al Registro Nazionale Consulenti ed Educatori Digitali Tecnologici e di Sostenibilità</h2>
              <p className="mt-3 text-[14px] text-[#b45309] font-semibold">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f59e0b]/15 border border-[#f59e0b]/30 text-[11px] uppercase tracking-wider">
                  <ShieldCheck className="h-3 w-3" /> Agg. al MIMIT tramite Centro Studi Esse
                </span>
              </p>
              <p className="mt-3 text-[14.5px] text-slate-600 max-w-2xl">
                Elenco aggiornato di tutti i professionisti iscritti al Registro Nazionale.
              </p>
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="registro-search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Cerca per nome, città, specializzazione..."
                className="pl-10 h-11"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#102a6b]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
              <Users className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-bold text-[14px]">Nessun iscritto corrisponde alla ricerca</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="registro-list">
              {filtered.map((i, idx) => {
                const iniziali = `${(i.nome || "")[0] || ""}${(i.cognome || "")[0] || ""}`.toUpperCase();
                const isAcdt = i.fonte === "A.C.D.T.";
                return (
                  <div key={`${i.nome}-${i.cognome}-${idx}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#102a6b]/40 hover:shadow-md transition-all">
                    <div className="flex items-start gap-3">
                      <div className={`h-12 w-12 rounded-2xl flex items-center justify-center font-extrabold text-white flex-shrink-0 ${isAcdt ? "bg-gradient-to-br from-[#102a6b] to-[#1e40af]" : "bg-gradient-to-br from-[#f59e0b] to-[#d97706]"}`}>
                        {iniziali || <Users className="h-5 w-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-extrabold text-[#0a1f55] text-[15px] leading-tight">{i.nome} {i.cognome}</h3>
                          <ShieldCheck className="h-4 w-4 text-emerald-600" />
                        </div>
                        {i.citta && (
                          <div className="text-[11.5px] text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" /> {i.citta}{i.provincia ? ` (${i.provincia})` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                    {i.specializzazione && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-extrabold">Specializzazione</div>
                        <div className="text-[12.5px] font-semibold text-[#102a6b] mt-0.5">{i.specializzazione}</div>
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${isAcdt ? "bg-[#102a6b]/10 text-[#102a6b]" : "bg-[#f59e0b]/15 text-[#b45309]"}`}>
                        {isAcdt ? <><Stamp className="h-3 w-3" /> A.C.D.T.</> : <><BookOpen className="h-3 w-3" /> Esse Studi</>}
                      </span>
                      {i.anno_iscrizione && (
                        <span className="text-[11px] text-slate-500">dal {i.anno_iscrizione}</span>
                      )}
                    </div>
                    {i.numero_tessera && (
                      <div className="mt-2 text-[10px] font-mono text-slate-500">{i.numero_tessera}</div>
                    )}
                    {i.codice_diploma && (
                      <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
                        <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-extrabold">Codice Diploma</div>
                        <div className="text-[10.5px] font-mono font-bold text-[#0a1f55] break-all">{i.codice_diploma}</div>
                        {i.data_scadenza && (
                          <div className="text-[10px] text-slate-500">Scadenza: <span className="font-bold text-slate-700">{new Date(i.data_scadenza).toLocaleDateString("it-IT")}</span></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && (
            <div className="mt-10 text-center">
              <p className="text-[12.5px] text-slate-500">
                Elenco aggiornato in tempo reale • {filtered.length} di {iscritti.length} iscritti mostrati
              </p>
            </div>
          )}
        </section>

        {/* CTA FINALE */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-16 md:pb-24">
          <div className="rounded-3xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-[#f59e0b]/25 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-[#3b82f6]/25 blur-3xl" />
            <div className="relative">
              <Mail className="h-10 w-10 text-[#fbbf24] mx-auto" />
              <h2 className="mt-4 text-3xl md:text-4xl font-extrabold">Pronto a iscriverti?</h2>
              <p className="mt-3 text-slate-200 max-w-2xl mx-auto">
                Diventa parte del Registro Nazionale dei Consulenti ed Educatori Digitali, Tecnologi e di Sostenibilità.
                Riconoscimento ufficiale, visibilità professionale, supporto continuo.
              </p>
              <div className="mt-7 flex flex-wrap gap-3 justify-center">
                <Link to="/area-soci" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold transition-colors">
                  Crea account A.C.D.T. <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/direttivo-documentazione" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold transition-colors">
                  Vedi documentazione
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {modalPacchetto && <IscrivitiModal pacchetto={modalPacchetto} onClose={() => setModalPacchetto(null)} />}

      <Footer />
    </>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div className={`rounded-xl px-4 py-3 border ${highlight ? "bg-[#f59e0b] text-[#0a1f55] border-[#f59e0b]" : "bg-white/10 text-white border-white/15 backdrop-blur-sm"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-extrabold ${highlight ? "text-[#7c2d12]" : "text-[#fbbf24]"}`}>{label}</div>
      <div className="mt-0.5 text-2xl font-extrabold">{value}</div>
    </div>
  );
}
